import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { EquipmentCategory, EquipmentCategoryForm, EquipmentCategorySearchForm } from 'entities/equipment_category'

type EquipmentCategorysResponse = PagingResponse & {
    results: EquipmentCategory[]
}

export function useFetchEquipmentCategoriesApi(
    searchForm?: Form<EquipmentCategorySearchForm>
): IndexApiSet<EquipmentCategorysResponse> & { execute: () => void } {
    const apiPath = 'equipment_categories/'
    const api = useIndexApi<EquipmentCategorysResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchEquipmentCategoryApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `equipment_categories/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostEquipmentCategoryApi(): ApiSet<BaseResponse> & { execute: (form: Form<EquipmentCategoryForm>) => void } {
    const apiSet = usePostApi<BaseResponse, EquipmentCategoryForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<EquipmentCategoryForm>) => {
        const apiPath = `equipment_categories/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutEquipmentCategoryApi(): ApiSet<BaseResponse> & { execute: (form: Form<EquipmentCategoryForm>) => void } {
    const apiSet = usePutApi<BaseResponse, EquipmentCategoryForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<EquipmentCategoryForm>) => {
        const apiPath = `equipment_categories/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutEquipmentCategorysApi(): ApiSet<BaseResponse> & { execute: (form: Form<EquipmentCategoryForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, EquipmentCategoryForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<EquipmentCategoryForm[]>) => {
        const apiPath = `equipment_categories/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteEquipmentCategoryApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `equipment_categories/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
